import Input from "../../ui/Input";
import clubStore from "../../storage/ClubStore";
import appStore from "../../storage/AppStore";
import Select from "../../ui/Select";
import { Calendar } from "primereact/calendar";
import Btn from "../../ui/Btn";
import ClickableMap from "../../ui/ClickableMap";
import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate, useLocation } from "react-router-dom";
import PageLayout from "../PageLayout";
import systemStore from "../../storage/SystemStore";
import "../../common/game.css";

const EMPTY_OPTION = { id: 0, name: "" };

const Game = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const game = clubStore.tournament.game;

  const [tournaments, setTournaments] = useState([]);
  const [tournamentCompanies, setTournamentCompanies] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [groups, setGroups] = useState([]);

  const [friendlyGuestCompanies, setFriendlyGuestCompanies] = useState([]);

  const [teamOptions1, setTeamOptions1] = useState([]);
  const [teamOptions2, setTeamOptions2] = useState([]);

  const [playerOptions1, setPlayerOptions1] = useState([]);
  const [playerOptions2, setPlayerOptions2] = useState([]);

  const currentCompanyId = Number(clubStore.tournament.company_id || 0);

  const sportMeta = useMemo(() => {
    const sports = clubStore.club?.sport_names || [];
    return sports.find((x) => String(x.id) === String(game.sport_id)) || null;
  }, [game.sport_id]);

  const sportType = useMemo(() => {
    const t =
      sportMeta?.type ||
      sportMeta?.sport_type ||
      sportMeta?.data?.type ||
      sportMeta?.data?.sport_type ||
      "team";
    return String(t).toLowerCase() === "individual" ? "individual" : "team";
  }, [sportMeta]);

  const isTournament = Number(game.tournament_id || 0) > 0;
  const isIndividual = sportType === "individual";
  const isTeam = !isIndividual;

  const selectedDivision = useMemo(() => {
    return (divisions || []).find((x) => Number(x.id) === Number(game.division_id)) || null;
  }, [divisions, game.division_id]);

  const divisionGroups = useMemo(() => {
    if (!selectedDivision) return [];
    return selectedDivision.groups || [];
  }, [selectedDivision]);

  const hasDivisionOptions = (divisions || []).length > 0;
  const hasGroupOptions = (divisionGroups || []).length > 0;

  const side1CompanyId = Number(game.company_id || 0);
  const side2CompanyId = Number(game.guest_company_id || 0);

  const resetParticipants = () => {
    game.setProperty("game_team_1_id", 0);
    game.setProperty("game_team_2_id", 0);
    game.setProperty("player_1_id", 0);
    game.setProperty("player_2_id", 0);
    setTeamOptions1([]);
    setTeamOptions2([]);
    setPlayerOptions1([]);
    setPlayerOptions2([]);
  };

  const resetTournamentStructure = () => {
    game.setProperty("division_id", 0);
    game.setProperty("group_id", 0);
    setDivisions([]);
    setGroups([]);
  };

  const loadOwnedTournaments = async () => {
    const tt = await clubStore.tournament.searchOwnedTournamentsForGame?.();
    setTournaments(tt || []);
  };

  const loadFriendlyGuestCompanies = async () => {
    const cc = await clubStore.tournament.getGuestCompanies(0);
    setFriendlyGuestCompanies(cc?.companies || []);
  };

  const loadTournamentContext = async (tournamentId) => {
    if (!tournamentId) {
      setTournamentCompanies([]);
      setDivisions([]);
      setGroups([]);
      return;
    }

    const [competitionRet, companiesRet] = await Promise.all([
      clubStore.tournament.getCompetition(tournamentId),
      clubStore.tournament.getGameCompaniesForTournament?.(tournamentId),
    ]);

    const competition = clubStore.tournament.competition;
    const nextDivisions = competition?.divisions || [];
    setDivisions(nextDivisions);

    const nextCompanies = companiesRet?.companies || [];
    setTournamentCompanies(nextCompanies);

    if (competition) {
      game.setProperty("address", competition.address || "");
      game.setProperty("sport_id", competition.sport_id || "");
      game.setModeFromSport?.(competition.sport_id || "");
      game.setProperty("location", competition.location || {});
    }

    return { competitionRet, companiesRet };
  };

  const getGroupOptionsFromDivisionId = (divisionId) => {
    const division = (divisions || []).find((x) => Number(x.id) === Number(divisionId));
    return division?.groups || [];
  };

  const tryAutofillDivisionAndGroupFromSides = () => {
    const findTeam = (options, id) =>
      (options || []).find((x) => Number(x.id) === Number(id));

    const team1 = findTeam(teamOptions1, game.game_team_1_id);
    const team2 = findTeam(teamOptions2, game.game_team_2_id);

    const div1 = Number(team1?.division_id || 0);
    const div2 = Number(team2?.division_id || 0);
    const grp1 = Number(team1?.group_id || 0);
    const grp2 = Number(team2?.group_id || 0);

    if (!Number(game.division_id || 0)) {
      if (div1 && div2 && div1 === div2) {
        game.setProperty("division_id", div1);
      } else if (div1 && !div2) {
        game.setProperty("division_id", div1);
      } else if (!div1 && div2) {
        game.setProperty("division_id", div2);
      }
    }

    if (!Number(game.group_id || 0)) {
      if (grp1 && grp2 && grp1 === grp2) {
        game.setProperty("group_id", grp1);
      } else if (grp1 && !grp2) {
        game.setProperty("group_id", grp1);
      } else if (!grp1 && grp2) {
        game.setProperty("group_id", grp2);
      }
    }
  };

  const loadTournamentTeamsForSide = async (side, companyId) => {
    if (!isTournament || !companyId) {
      if (side === 1) setTeamOptions1([]);
      else setTeamOptions2([]);
      return;
    }

    const ret = await clubStore.tournament.getTournamentTeamsForGame?.({
      tournament_id: game.tournament_id,
      company_id: companyId,
      division_id: game.division_id || null,
      group_id: game.group_id || null,
    });

    const items = ret?.teams || [];
    if (side === 1) setTeamOptions1(items);
    else setTeamOptions2(items);
  };

  const loadFriendlyTeamsForSide = async (side, companyId) => {
    if (!companyId) {
      if (side === 1) setTeamOptions1([]);
      else setTeamOptions2([]);
      return;
    }

    const ret = await clubStore.tournament.getTeamsForCompany(companyId);
    const items = ret?.teams || [];
    if (side === 1) setTeamOptions1(items);
    else setTeamOptions2(items);
  };

  const loadTournamentPlayersForSide = async (side, companyId) => {
    if (!isTournament || !companyId) {
      if (side === 1) setPlayerOptions1([]);
      else setPlayerOptions2([]);
      return;
    }

    const ret = await clubStore.tournament.getTournamentPlayersForGame?.({
      tournament_id: game.tournament_id,
      company_id: companyId,
      division_id: game.division_id || null,
      group_id: game.group_id || null,
    });

    const items = ret?.members || [];
    if (side === 1) setPlayerOptions1(items);
    else setPlayerOptions2(items);
  };

  const loadFriendlyPlayersForSide = async (side, companyId) => {
    if (!companyId) {
      if (side === 1) setPlayerOptions1([]);
      else setPlayerOptions2([]);
      return;
    }

    const ret = await clubStore.tournament.getCompanyMembersDropdown(companyId);
    const items = ret?.members || [];
    if (side === 1) setPlayerOptions1(items);
    else setPlayerOptions2(items);
  };

  const loadSideOptions = async () => {
    if (isTeam) {
      if (isTournament) {
        await Promise.all([
          loadTournamentTeamsForSide(1, side1CompanyId),
          loadTournamentTeamsForSide(2, side2CompanyId),
        ]);
      } else {
        await Promise.all([
          loadFriendlyTeamsForSide(1, side1CompanyId),
          loadFriendlyTeamsForSide(2, side2CompanyId),
        ]);
      }
    } else {
      if (isTournament) {
        await Promise.all([
          loadTournamentPlayersForSide(1, side1CompanyId),
          loadTournamentPlayersForSide(2, side2CompanyId),
        ]);
      } else {
        await Promise.all([
          loadFriendlyPlayersForSide(1, side1CompanyId),
          loadFriendlyPlayersForSide(2, side2CompanyId),
        ]);
      }
    }
  };

  useEffect(() => {
    loadOwnedTournaments();
    loadFriendlyGuestCompanies();
  }, [location.pathname]);

  useEffect(() => {
    if (!isTournament) {
      resetTournamentStructure();
      game.setProperty("company_id", currentCompanyId);
      setTournamentCompanies([]);
      setDivisions([]);
      setGroups([]);
    }
  }, [game.tournament_id, currentCompanyId]);

  useEffect(() => {
    if (!game.tournament_id) return;

    (async () => {
      await loadTournamentContext(Number(game.tournament_id));

      // keep existing division if valid
      const nextDivisionId = Number(game.division_id || 0);
      const nextGroups = getGroupOptionsFromDivisionId(nextDivisionId);
      setGroups(nextGroups);
    })();
  }, [game.tournament_id]);

  useEffect(() => {
    const nextGroups = getGroupOptionsFromDivisionId(game.division_id);
    setGroups(nextGroups);

    if (
      Number(game.group_id || 0) > 0 &&
      !nextGroups.find((g) => Number(g.id) === Number(game.group_id))
    ) {
      game.setProperty("group_id", 0);
    }

    resetParticipants();
  }, [game.division_id]);

  useEffect(() => {
    resetParticipants();
  }, [game.group_id, game.sport_id]);

  useEffect(() => {
    if (!isTournament) {
      game.setProperty("company_id", currentCompanyId);
    }
  }, [isTournament, currentCompanyId]);

  useEffect(() => {
    loadSideOptions();
  }, [
    isTournament,
    isTeam,
    isIndividual,
    side1CompanyId,
    side2CompanyId,
    game.division_id,
    game.group_id,
    game.tournament_id,
  ]);

  useEffect(() => {
    tryAutofillDivisionAndGroupFromSides();
  }, [game.game_team_1_id, game.game_team_2_id, teamOptions1, teamOptions2]);

  const tournamentCompanyOptions = useMemo(() => {
    return (tournamentCompanies || []).map((c) => ({
      id: c.company_id || c.id,
      name: c.name,
    }));
  }, [tournamentCompanies]);

  const divisionOptions = useMemo(() => {
    return (divisions || []).map((d) => ({
      id: d.id,
      name: d.name,
    }));
  }, [divisions]);

  const groupOptions = useMemo(() => {
    return [{ id: 0, name: appStore.tr.playoff_no_group || appStore.tr.no_group }].concat(
      (groups || []).map((g) => ({
        id: g.id,
        name: g.name,
      }))
    );
  }, [groups]);

  const onTournamentChange = async (v) => {
    const tournamentId = Number(v || 0);

    game.setProperty("tournament_id", tournamentId);

    resetTournamentStructure();
    resetParticipants();

    game.setProperty("guest_company_id", 0);
    game.setProperty("player_1_id", 0);
    game.setProperty("player_2_id", 0);

    if (!tournamentId) {
      game.setProperty("company_id", currentCompanyId);
      return;
    }

    game.setProperty("company_id", 0);
    game.setProperty("guest_company_id", 0);

    await loadTournamentContext(tournamentId);
  };

  const onSportChange = (v) => {
    game.setProperty("sport_id", v);
    game.setModeFromSport?.(v);
    resetParticipants();
  };

  const onDivisionChange = (v) => {
    game.setProperty("division_id", Number(v || 0));
    game.setProperty("group_id", 0);
  };

  const onGroupChange = (v) => {
    game.setProperty("group_id", Number(v || 0));
  };

  const onSide1CompanyChange = async (v) => {
    const nextId = Number(v || 0);
    game.setProperty("company_id", nextId);
    if (!isTournament && nextId !== currentCompanyId) {
      game.setProperty("company_id", currentCompanyId);
      return;
    }

    if (Number(game.guest_company_id || 0) === nextId) {
      game.setProperty("guest_company_id", 0);
    }

    game.setProperty("game_team_1_id", 0);
    game.setProperty("player_1_id", 0);
    await loadSideOptions();
  };

  const onSide2CompanyChange = async (v) => {
    const nextId = Number(v || 0);
    game.setProperty("guest_company_id", nextId);

    if (Number(game.company_id || 0) === nextId) {
      game.setProperty("company_id", 0);
    }

    game.setProperty("game_team_2_id", 0);
    game.setProperty("player_2_id", 0);
    await loadSideOptions();
  };

  const validateBeforeSave = () => {
    if (isTournament) {
      if (hasDivisionOptions && !Number(game.division_id || 0)) {
        systemStore.setError(appStore.tr.select_division);
        return false;
      }

      if (!Number(game.company_id || 0) || !Number(game.guest_company_id || 0)) {
        systemStore.setError(appStore.tr.select_both_clubs || "Select both clubs");
        return false;
      }

      if (Number(game.company_id) === Number(game.guest_company_id)) {
        systemStore.setError(appStore.tr.side_clubs_cannot_be_same);
        return false;
      }
    } else {
      if (Number(game.company_id || 0) !== currentCompanyId) {
        game.setProperty("company_id", currentCompanyId);
      }
      if (!Number(game.guest_company_id || 0)) {
        systemStore.setError(appStore.tr.select_rival_club || "Select rival club");
        return false;
      }
    }

    if (isTeam) {
      if (!Number(game.game_team_1_id || 0) || !Number(game.game_team_2_id || 0)) {
        systemStore.setError(appStore.tr.select_both_teams || "Select both teams");
        return false;
      }
      if (Number(game.game_team_1_id) === Number(game.game_team_2_id)) {
        systemStore.setError(appStore.tr.side_teams_cannot_be_same);
        return false;
      }
    } else {
      if (!Number(game.player_1_id || 0) || !Number(game.player_2_id || 0)) {
        systemStore.setError(appStore.tr.select_both_players || "Select both players");
        return false;
      }
      if (Number(game.player_1_id) === Number(game.player_2_id)) {
        systemStore.setError(appStore.tr.same_player_not_allowed || "Players must be different");
        return false;
      }
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateBeforeSave()) return;

    const ret = await clubStore.tournament.saveGame();
    if (ret && ret.result === 0) {
      clubStore.tournament.searchGames();
      navigate(`/manager/games/list`);
    }
  };

  return (
    <PageLayout header={
      <div className="competition-form__page-heading">
        <span className="competition-form__page-icon"><i className="pi pi-flag" aria-hidden="true"/></span>
        <h3 className="competition-form__page-title">{game.id > 0 ? game.name : appStore.tr.add_game}</h3>
      </div>
    }>
      <div className="competition-form">
        <section className="competition-form__section">
          <div className="competition-form__section-heading">
            <span className="competition-form__section-icon"><i className="pi pi-info-circle" aria-hidden="true"/></span>
            <h4>{appStore.tr.name} / {appStore.tr.competition}</h4>
          </div>

          <div className="competition-form__grid">
            <div className="competition-form__field competition-form__field--wide">
              <label htmlFor="name">{appStore.tr.name}</label>
              <Input store={game} prop="name" label={appStore.tr.name} id="name"/>
            </div>
            <div className="competition-form__field">
              <label htmlFor="tournament">{appStore.tr.competition}</label>
              <Select store={game} prop="tournament_id" options={tournaments} id="tournament"
                      label={appStore.tr.competition} onChange={onTournamentChange}/>
              {!isTournament && <small className="text-orange-500 font-bold">{appStore.tr.friendly_match}</small>}
            </div>
            <div className="competition-form__field">
              <label htmlFor="sport_id">{appStore.tr.sport}</label>
              <Select store={game} prop="sport_id" label={appStore.tr.sport}
                      options={clubStore.club.sport_names} id="sport_id"
                      onChange={onSportChange} disabled={isTournament}/>
            </div>
            {isTournament && hasDivisionOptions && (
              <div className="competition-form__field">
                <label htmlFor="division_id">{appStore.tr.division}</label>
                <Select store={game} prop="division_id" label={appStore.tr.division}
                        options={divisionOptions} id="division_id" onChange={onDivisionChange}/>
              </div>
            )}
            {isTournament && hasGroupOptions && (
              <div className="competition-form__field">
                <label htmlFor="group_id">{appStore.tr.group}</label>
                <Select store={game} prop="group_id" label={appStore.tr.group}
                        options={groupOptions} id="group_id" onChange={onGroupChange}/>
                <small className="text-600">{appStore.tr.group_optional || "Group is optional for playoff games"}</small>
              </div>
            )}
          </div>
        </section>

        <section className="competition-form__section">
          <div className="competition-form__section-heading">
            <span className="competition-form__section-icon"><i className="pi pi-users" aria-hidden="true"/></span>
            <h4>{appStore.tr.club_1} / {appStore.tr.club_2}</h4>
          </div>

          <div className="competition-form__grid">
            {isTournament ? (
              <>
                <div className="competition-form__field">
                  <label htmlFor="company_id">{appStore.tr.club_1}</label>
                  <Select store={game} prop="company_id" label={appStore.tr.club_1}
                          options={tournamentCompanyOptions} id="company_id" onChange={onSide1CompanyChange}/>
                </div>
                <div className="competition-form__field">
                  <label htmlFor="guest_company_id">{appStore.tr.club_2}</label>
                  <Select store={game} prop="guest_company_id" label={appStore.tr.club_2}
                          options={tournamentCompanyOptions} id="guest_company_id" onChange={onSide2CompanyChange}/>
                </div>
              </>
            ) : (
              <>
                <div className="competition-form__field">
                  <label htmlFor="home_company">{appStore.tr.home_club}</label>
                  <Input value={clubStore.club?.name || ""} id="home_company" disabled/>
                </div>
                <div className="competition-form__field">
                  <label htmlFor="guest_company_id">{appStore.tr.rival}</label>
                  <Select store={game} prop="guest_company_id" label={appStore.tr.rival}
                          options={friendlyGuestCompanies} id="guest_company_id" onChange={onSide2CompanyChange}/>
                </div>
              </>
            )}

            {isTeam ? (
              <>
                <div className="competition-form__field">
                  <label htmlFor="game_team_1_id">{isTournament ? appStore.tr.team_1 : appStore.tr.home_team}</label>
                  <Select store={game} prop="game_team_1_id"
                          label={isTournament ? appStore.tr.team_1 : appStore.tr.home_team}
                          options={teamOptions1} id="game_team_1_id"
                          onChange={(v) => game.setProperty("game_team_1_id", Number(v || 0))}/>
                </div>
                <div className="competition-form__field">
                  <label htmlFor="game_team_2_id">{isTournament ? appStore.tr.team_2 : appStore.tr.away_team}</label>
                  <Select store={game} prop="game_team_2_id"
                          label={isTournament ? appStore.tr.team_2 : appStore.tr.away_team}
                          options={teamOptions2} id="game_team_2_id"
                          onChange={(v) => game.setProperty("game_team_2_id", Number(v || 0))}/>
                </div>
              </>
            ) : (
              <>
                <div className="competition-form__field">
                  <label htmlFor="player_1_id">{isTournament ? appStore.tr.player_1 : appStore.tr.home_player}</label>
                  <Select store={game} prop="player_1_id"
                          label={isTournament ? appStore.tr.player_1 : appStore.tr.home_player}
                          options={playerOptions1} id="player_1_id"
                          onChange={(v) => game.setProperty("player_1_id", Number(v || 0))}/>
                </div>
                <div className="competition-form__field">
                  <label htmlFor="player_2_id">{isTournament ? appStore.tr.player_2 : appStore.tr.away_player}</label>
                  <Select store={game} prop="player_2_id"
                          label={isTournament ? appStore.tr.player_2 : appStore.tr.away_player}
                          options={playerOptions2} id="player_2_id"
                          onChange={(v) => game.setProperty("player_2_id", Number(v || 0))}/>
                </div>
              </>
            )}
          </div>
        </section>

        <section className="competition-form__section">
          <div className="competition-form__section-heading">
            <span className="competition-form__section-icon"><i className="pi pi-calendar" aria-hidden="true"/></span>
            <h4>{appStore.tr.beginning} / {appStore.tr.fee}</h4>
          </div>
          <div className="competition-form__grid">
            <div className="competition-form__field">
              <label htmlFor="start_time">{appStore.tr.beginning}</label>
              <Calendar className="p-inputtext-sm" value={game.start_time}
                        onChange={(e) => game.setProperty("start_time", e.value)}
                        dateFormat="dd.mm.yy" showTime hourFormat="24" id="start_time"/>
            </div>
            <div className="competition-form__field">
              <label htmlFor="fee">{appStore.tr.fee}</label>
              <Input type="number" label={appStore.tr.fee} store={game} prop="fee" id="fee"/>
            </div>
            <div className="competition-form__field competition-form__field--wide">
              <label htmlFor="address">{appStore.tr.address}</label>
              <Input store={game} prop="address" label={appStore.tr.address} id="address"/>
            </div>
          </div>
        </section>

        <section className="competition-form__section">
          <div className="competition-form__section-heading">
            <span className="competition-form__section-icon"><i className="pi pi-map-marker" aria-hidden="true"/></span>
            <h4>{appStore.tr.location}</h4>
          </div>
          <div className="competition-form__field competition-form__map">
            <label htmlFor="input_location">{appStore.tr.location}</label>
            <ClickableMap id="input_location" center={clubStore.club.location} zoom={12}
                          store={game} prop="location"/>
          </div>
        </section>

        <div className="competition-form__footer">
          <Btn icon="pi pi-save" severity="success" className="competition-form__save"
               onClick={handleSave} label={appStore.tr.save} disabled={!game.isBaseValid}/>
        </div>
      </div>
    </PageLayout>
  );
};

export default observer(Game);
